import uniq from 'lodash/uniq';

import type { UnappliedFiltersByFacet } from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import type { SearchFilterOption } from 'bundles/search-common/types';

const mappingTo: Record<string, string> = {
  Courses: 'Courses',
  'Professional Certificates': 'Professional Certificates',
  Specializations: 'Specializations',
  Projects: 'Projects',
  'Guided Projects': 'Projects',
  Degrees: 'Degrees',
  'MasterTrack® Certificates': 'Postgraduate and graduate diplomas',
  'Postgraduate Diploma': 'Postgraduate and graduate diplomas',
  'Graduate Certificates': 'Postgraduate and graduate diplomas',
  'University Certificates': 'Postgraduate and graduate diplomas',
};

const mappingFrom: Record<string, string[]> = {
  Courses: ['Courses'],
  'Professional Certificates': ['Professional Certificates'],
  Specializations: ['Specializations'],
  Projects: ['Projects', 'Guided Projects'],
  Degrees: ['Degrees'],
  'Postgraduate and graduate diplomas': [
    'MasterTrack® Certificates',
    'Postgraduate Diploma',
    'Graduate Certificates',
    'University Certificates',
  ],
};

function splitFilter(filter: string) {
  const [category, value] = filter.split(':');
  return { category, value };
}

function joinFilter(category: string, value: string) {
  return `${category}:${value}`;
}

type AdaptedValues = {
  groupValues: SearchFilterOption[];
  onFilterChange: (unappliedFilters: UnappliedFiltersByFacet) => void;
  unappliedFilters: string[];
};

type Adapter = {
  adaptGroupValues: (groupValues: SearchFilterOption[] | undefined) => SearchFilterOption[];
  adaptUnappliedFiltersByFacet: (newFilters: UnappliedFiltersByFacet) => UnappliedFiltersByFacet;
  adaptUnappliedFilters: (unappliedFilters: string[] | undefined) => string[];
};

export const adapters: Record<string, Adapter> = {
  productTypeDescription: {
    adaptGroupValues: (groupValues: SearchFilterOption[] | undefined) => {
      const adaptedValues: Record<string, SearchFilterOption> =
        groupValues?.reduce((acc: Record<string, SearchFilterOption>, option) => {
          const adapted = mappingTo[option.value];
          return {
            ...acc,
            // @ts-expect-error TS2464 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            [adapted]: {
              ...option,
              // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              isRefined: option.isRefined || (acc?.[adapted]?.isRefined ?? false),
              // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              count: option.count + (acc?.[adapted]?.count ?? 0),
              label: adapted,
              value: adapted,
            },
          };
        }, {}) ?? {};
      return Object.values(adaptedValues);
    },
    adaptUnappliedFiltersByFacet: (newFilters: UnappliedFiltersByFacet) => ({
      ...newFilters,
      productTypeDescription: Array.from(
        newFilters?.productTypeDescription?.reduce((acc: string[], v) => {
          const { category, value } = splitFilter(v);
          // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          return acc.concat(mappingFrom[value]?.map((newLabel) => joinFilter(category, newLabel)));
        }, []) ?? []
      ),
    }),
    adaptUnappliedFilters: (unappliedFilters: string[] | undefined) =>
      uniq(
        unappliedFilters?.reduce((acc: string[], v) => {
          const { category, value } = splitFilter(v);
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          return [...acc, joinFilter(category, mappingTo[value])];
        }, []) ?? []
      ),
  },
};

export function filterValueAdapter(
  filterGroupKey: string,
  groupValues: SearchFilterOption[] | undefined,
  unappliedFilters: string[] | undefined,
  onFilterChange: (newFilters: UnappliedFiltersByFacet) => void
): AdaptedValues {
  const adapter = adapters[filterGroupKey];
  if (adapter) {
    return {
      groupValues: adapter.adaptGroupValues(groupValues),
      onFilterChange: (newFilters: UnappliedFiltersByFacet) => {
        onFilterChange(adapter.adaptUnappliedFiltersByFacet(newFilters));
      },
      unappliedFilters: adapter.adaptUnappliedFilters(unappliedFilters),
    };
  } else {
    return {
      groupValues: groupValues ?? [],
      onFilterChange,
      unappliedFilters: unappliedFilters ?? [],
    };
  }
}

export function userFiltersAdapter(userFilters: string[][] | undefined) {
  return userFilters?.map((filters) =>
    uniq(
      filters.map((filter) => {
        const { category, value } = splitFilter(filter);
        switch (category) {
          case 'productTypeDescription': {
            // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            return joinFilter(category, mappingTo[value]);
          }
          default:
            return filter;
        }
      })
    )
  );
}
